import Splide from '@splidejs/splide';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import Vue from '../../assets/vue';
import { setMaxHeightStyle } from '../../scripts/utils/setMaxHeightStyle';
import { addItems } from '../../scripts/cartApi';
import { sendCustomEvent } from '../../scripts/config/analytics';
import {
  updateAvailabilityAddToCartNavBtns,
  forceProductVideoStart,
  videoToggle,
  videoEnded,
  getVariantIdByUrl,
  formatPriceWithoutZeros,
  normalizeProductConfig,
  addOffsetToStickyProductSlider,
  updateProductUrl,
  calcPreOrderMessage,
  calcSalePrice,
  calcNetPrice,
  ADD_PRODUCT_TO_CART_ERROR_MSG,
} from '../../scripts/utils/product-helpers';
import { updateProductVariantInShopPayTerms } from '../../scripts/utils/shoppay-helpers';
import {
  initCart,
  openSideCart,
} from '../../templates/common/side-cart/side-cart';
import {
  initProductBuyBanner,
  showBannerSpinner,
} from '../../templates/common/product-buy-banner/product-buy-banner';
import { calcBogoBonusProducts } from '../../scripts/utils/bogo-helpers';
import {
  ADD_TO_CART_TEXT,
  NOT_AVAILABLE_TEXT,
  PRE_ORDER_TEXT,
} from '../../scripts/config/constants';
import { initProductWishListModal } from '../product-wish-list-modal/product-wish-list-modal';

const { DeviceHelper, productsConfig } = window;

const initSlider = (selector) =>
  new Splide(selector, {
    type: 'loop',
    pagination: true,
    arrows: 'slider',
    accessibility: true,
    trimSpace: true,
    perMove: 1,
    flickVelocityThreshold: 0.5,
    flickPower: 500,
    slideFocus: false,
    updateOnMove: true,
    cover: false,
    focusableNodes: 'button',
    lazyLoad: 'nearby',
    classes: {
      pagination: 'product-base__slider-main-pagination',
      page: 'product-base__slider-main-pagination-btn',
    },
  });

const initThumbSlider = (selector) =>
  new Splide(selector, {
    type: 'loop',
    direction: 'ttb',
    height: '100%',
    gap: 10,
    pagination: false,
    accessibility: true,
    isNavigation: true,
    perPage: 6,
    flickVelocityThreshold: 0.5,
    flickPower: 500,
    wheel: true,
    wheelSleep: 300,
    lazyLoad: 'nearby',
  });

const Product = {
  data() {
    const { activeProductId } = productsConfig;
    const productActiveConfig = productsConfig.products[activeProductId];
    const productActiveMeta = productsConfig.productsMeta[activeProductId];
    const normalizedProductActive = normalizeProductConfig(productActiveConfig);
    const variantId = getVariantIdByUrl(productActiveConfig);
    const variantActive =
      normalizedProductActive.variants.find(({ id }) => id === variantId) ||
      normalizedProductActive.variants[0];
    const isTabletViewport = DeviceHelper.isTabletViewport();

    this.swatchesList = normalizedProductActive.variants.reduce(
      (swatchesData, variant) => {
        swatchesData.push({
          available: this.isVariantInStock(variant),
          variantId: variant.id,
          optionValue: variant.handleizedOption1,
        });

        return swatchesData;
      },
      []
    );

    return {
      isTabletViewport,
      normalizedProductActive,
      productActiveMeta,
      variantActive,
      isSpinnerMainProductActive: false,
      isDetailsBlockClose: true,
      isDescriptionBlockClose: true,
      isBenefitsBlockClose: true,
      errorMessage: '',
      loaded: false,
    };
  },
  computed: {
    sliderMedias() {
      return productsConfig.variantsMedia[this.variantActive.id].sliderMedia;
    },
    selectPanelMedia() {
      return productsConfig.variantsMedia[this.variantActive.id]
        .selectModelMedia;
    },
    variantActiveMeta() {
      return productsConfig.variantsMeta[this.variantActive.id];
    },
    variantActiveInventory() {
      return productsConfig.variantsInventory[this.variantActive.id];
    },
    netPrice() {
      const { variantActive, discountPrice } = this;

      return calcNetPrice(
        variantActive.price,
        variantActive.compare_at_price,
        discountPrice
      );
    },
    formattedNetPrice() {
      return formatPriceWithoutZeros(this.netPrice);
    },
    discountPrice() {
      const { discountPrice } =
        productsConfig.variantsMeta[this.variantActive.id];

      return discountPrice;
    },
    salePrice() {
      const { discountPrice } = this;
      const { price } = this.variantActive;

      return calcSalePrice(price, discountPrice);
    },
    formattedSalePrice() {
      return formatPriceWithoutZeros(this.salePrice);
    },
    preOrderMessage() {
      const { preOrderText } = this.variantActiveMeta;
      const { inventoryQuantity, inventoryPolicy } =
        this.variantActiveInventory;

      return calcPreOrderMessage(
        preOrderText,
        inventoryQuantity,
        inventoryPolicy
      );
    },
    submitButtonText() {
      if (!this.isVariantActiveAvailable) {
        return this.variantActiveMeta.notAvailableText || NOT_AVAILABLE_TEXT;
      }

      if (this.preOrderMessage) {
        return PRE_ORDER_TEXT;
      }

      return ADD_TO_CART_TEXT;
    },
    isVariantActiveAvailable() {
      const isActiveVariantInStock = this.isVariantInStock(this.variantActive);
      const isErrorEmpty = this.errorMessage === '';

      return isActiveVariantInStock && isErrorEmpty;
    },
  },
  methods: {
    productToggleVideo(event) {
      const element = event.currentTarget;

      videoToggle(element);
    },
    productEndedVideo(event) {
      const videoEl = event.target;

      videoEnded(videoEl);
    },
    isVariantInStock(variant) {
      return variant.available;
    },
    changeActiveVariant(variantId) {
      const variantNext =
        this.normalizedProductActive.variants.find(
          (variant) => variant.id === variantId
        ) || null;

      if (variantNext) {
        this.variantActive = variantNext;
        this.errorMessage = '';
      }

      Vue.nextTick(() => {
        this.imagesSliderMain.refresh();
        this.imagesSliderThumbs.refresh();
        this.imagesSliderMain.go(0);

        updateProductUrl(
          this.variantActive.option1,
          this.normalizedProductActive.url
        );

        setMaxHeightStyle(
          '[data-product-base-product-mobile-panel-features-content]'
        );

        updateProductVariantInShopPayTerms(`[data-shop-pay-terms-wrapper]`);
      });
    },
    calcVariantsBuyList() {
      const mainProducts = [
        {
          id: Number(this.variantActive.id),
          quantity: 1,
          properties: {},
        },
      ];

      const bogoProducts = calcBogoBonusProducts(
        productsConfig.bogo[this.variantActive.id]
      );

      if (bogoProducts) {
        return [...bogoProducts, ...mainProducts];
      }

      return mainProducts;
    },
    async submitProductForm(e) {
      e.preventDefault();

      this.isSpinnerMainProductActive = true;
      showBannerSpinner(true);

      await initCart();

      const items = this.calcVariantsBuyList();

      try {
        await addItems(items);

        window.sideCart.updateView();
        openSideCart();

        const addedVariantsIds = items.map((item) => item.id);

        addedVariantsIds.forEach((addedVariantId) => {
          sendCustomEvent({
            category: 'Cart',
            action: 'Add',
            label: addedVariantId,
          });
        });
      } catch (err) {
        if (err.headers) {
          err.json().then((body) => {
            this.errorMessage = body.message;
          });
        } else {
          this.errorMessage = ADD_PRODUCT_TO_CART_ERROR_MSG;
        }
      }

      this.isSpinnerMainProductActive = false;
      showBannerSpinner(false);
    },
    openWishListModal() {
      window.productWishListModal?.openModal();
    },
  },
  mounted() {
    updateAvailabilityAddToCartNavBtns();

    const variantId = getVariantIdByUrl(
      productsConfig.products[productsConfig.activeProductId]
    );

    updateProductUrl(variantId, this.normalizedProductActive.url);

    updateProductVariantInShopPayTerms(`[data-shop-pay-terms-wrapper]`);

    const mainSliderEl = document.querySelector('[data-product-slider-main]');
    const thumbsSliderEL = document.querySelector(
      '[data-product-slider-thumbs]'
    );

    if (mainSliderEl && thumbsSliderEL) {
      this.imagesSliderMain = initSlider('[data-product-slider-main]');
      this.imagesSliderThumbs = initThumbSlider('[data-product-slider-thumbs]');

      if (this.imagesSliderMain) {
        this.imagesSliderMain.on('active', ({ index }) => {
          const isVideoType = this.sliderMedias[index]?.type === 'video';

          if (isVideoType) {
            forceProductVideoStart(index, DeviceHelper.isMobileViewport());
          }
        });

        this.imagesSliderMain.on('lazyload:loaded', () => {
          this.loaded = true;
        });
        this.imagesSliderMain.sync(this.imagesSliderThumbs);
        this.imagesSliderMain.mount();
        this.imagesSliderThumbs.mount();
      }
    }

    forceProductVideoStart(0, DeviceHelper.isMobileViewport());

    window.addEventListener('load', () => {
      if (!this.isTabletViewport && mainSliderEl) {
        const mainImageSliderHeight = mainSliderEl.offsetHeight;

        thumbsSliderEL.style.height = `${mainImageSliderHeight}px`;
      }

      initProductBuyBanner({
        productId: this.normalizedProductActive.id,
        variantId: this.variantActive.id,
        swatchesList: this.swatchesList,
        formattedSalePrice: this.formattedSalePrice || null,
        hasCrossedPrice: Boolean(
          this.discountPrice || this.variantActive.compare_at_price
        ),
        formattedNetPrice: this.formattedNetPrice || null,
        isVariantActiveAvailable: this.isVariantActiveAvailable,
        submitButtonText: this.submitButtonText,
      });

      initProductWishListModal({
        variantId: this.variantActive.id,
      });
    });

    window.addEventListener(
      'resize',
      throttle(() => {
        this.isTabletViewport = DeviceHelper.isTabletViewport();
        if (!this.isTabletViewport && mainSliderEl) {
          const mainImageSliderHeight = mainSliderEl.offsetHeight;

          thumbsSliderEL.style.height = `${mainImageSliderHeight}px`;
        }
      }, 100)
    );
  },
  updated() {
    const { productWishListModal, productBuyBanner } = window;

    productBuyBanner?.updateBannerStates({
      newProductId: this.normalizedProductActive.id,
      newVariantId: this.variantActive.id,
      newSwatchesList: this.swatchesList,
      newFormattedSalePrice: this.formattedSalePrice || null,
      hasCrossedPrice: Boolean(
        this.discountPrice || this.variantActive.compare_at_price
      ),
      newFormattedNetPrice: this.formattedNetPrice || null,
      isNewVariantAvailable: this.isVariantActiveAvailable,
      newSubmitButtonText: this.submitButtonText,
    });

    if (!this.isVariantActiveAvailable) {
      productWishListModal?.updateModalState({
        newVariantId: this.variantActive.id,
      });
    }
  },
};

export const initProduct = () => {
  if (Product) {
    new Vue(Product).$mount('#product-base');
  }

  window.addEventListener(
    'resize',
    debounce(() => {
      setMaxHeightStyle(
        '[data-product-base-product-mobile-panel-features-content]'
      );
      addOffsetToStickyProductSlider('[data-product-base-sticky-gallery]');
    }, 50)
  );

  window.addEventListener(
    'scroll',
    throttle(() => {
      addOffsetToStickyProductSlider('[data-product-base-sticky-gallery]');
    }, 100)
  );

  document.addEventListener('DOMContentLoaded', () => {
    setMaxHeightStyle(
      '[data-product-base-product-mobile-panel-features-content]'
    );
    addOffsetToStickyProductSlider('[data-product-base-sticky-gallery]');
  });
};
